import { useRef, useEffect } from "react";

/** Space left between a comment being edited and the bottom of the activity - the composer below it. */
export const EDIT_GAP_PX = 12;

/** Marks the activity while a comment in it is edited - main.css then takes its desktop bottom padding down to the gap. */
export const EDITING_PANE_CLASS = 'editing-comment';

/** How far an element's bottom runs past `gap` above its pane's visible bottom. 0 when it already fits. */
export const bottomOverflow = (elementBottom: number, paneBottom: number, gap: number = EDIT_GAP_PX): number =>
    Math.max(0, Math.round(elementBottom - (paneBottom - gap)));

/**
 * Where to scroll the pane so an element running `overflow` past the gap shows whole: never past the pane's end, and
 * all the way to it when what is left is within its bottom padding - where the activity's scroll pin leaves the last
 * comment - so the two land in the same place.
 */
export const revealScrollTop = (scrollTop: number, maxScrollTop: number, overflow: number, panePaddingBottom: number): number => {
    const top = scrollTop + overflow;
    return maxScrollTop - top < panePaddingBottom ? maxScrollTop : top;
};

const scrollPane = (el: HTMLElement): HTMLElement | null => {
    for (let parent = el.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) return parent;
    }
    return null;
};

/**
 * Keeps a comment being edited in view: scrolled to when it opens, and its bottom bar kept above the composer as
 * Enter makes it taller - the activity's scroll pin only follows a reader at the bottom. It ends where the pin ends
 * the last comment: lining up with a different edge left the box a padding's height above where the pin then
 * snapped it on the first Enter (Shaun, 2026-10-20).
 */
export function useScrollIntoViewOnEdit(isEditing: boolean) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const box = containerRef.current;
        const pane = box && scrollPane(box);
        if (!isEditing || !pane || typeof ResizeObserver === 'undefined') return;

        pane.classList.add(EDITING_PANE_CLASS);
        const reveal = (behavior: ScrollBehavior) => {
            const overflow = bottomOverflow(box.getBoundingClientRect().bottom, pane.getBoundingClientRect().bottom);
            if (overflow === 0) return;
            const padding = parseFloat(getComputedStyle(pane).paddingBottom) || 0;
            pane.scrollTo({ top: revealScrollTop(pane.scrollTop, pane.scrollHeight - pane.clientHeight, overflow, padding), behavior });
        };

        const id = setTimeout(() => reveal('smooth'), 50);
        const grows = new ResizeObserver(() => reveal('auto'));
        grows.observe(box);
        return () => {
            clearTimeout(id);
            grows.disconnect();
            pane.classList.remove(EDITING_PANE_CLASS);
        };
    }, [isEditing]);

    return containerRef;
}
